import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AiCostBreakdown } from './ai-cost.types';
import { AiLatestCallsResponse } from './ai-evaluations.types';

/** Which application the call belongs to — mirrors `_TARGET_KINDS` in views.py. */
export type AiCallProduct = 'kickstart' | 'df' | 'pre-application';

/**
 * One application's AI calls as Ops reads them: the newest call of each purpose
 * with the bodies that went to the model, and the costed breakdown.
 *
 * Ops surface (N4 — model, prompt and cost are internal). Separate from
 * `AiEvaluationsService`, which is the system-admin log: that one is keyed on a
 * call's own short_id, this one on the application's, so an Ops user only ever
 * reaches calls on a file they can already open.
 */
@Injectable({ providedIn: 'root' })
export class AiCallService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/ai/calls`;

  /**
   * The latest call of each purpose for this application; 404 when nothing has
   * been logged.
   *
   * A pre-application's indicative-sizing assessments are NOT in its own scope —
   * they run against the Kickstart draft — so a caller that wants them asks
   * again with `'kickstart'` and the draft's `indicative_ks`. That second
   * request is deliberate: the backend's target map is shared with the cost
   * view, so widening it would move the cost figures too.
   */
  latestForApplication(
    product: AiCallProduct,
    targetId: string,
  ): Observable<AiLatestCallsResponse> {
    return this.http.get<AiLatestCallsResponse>(`${this.base}/${product}/${targetId}/latest/`);
  }

  /** All costed AI calls for one application (and its documents) + totals. */
  costForApplication(product: AiCallProduct, targetId: string): Observable<AiCostBreakdown> {
    return this.http.get<AiCostBreakdown>(
      `${environment.apiUrl}/api/ai/cost/${product}/${targetId}/`,
    );
  }
}
